import request from '@/util/request'
import {FulfillmentTypes, UNITED_STATES} from '@/util/constants'

import _ from 'lodash'
import {default as productMixin} from '@/mixins/product'
import {createHelpers} from 'vuex-map-fields'
import { EVENT_PROVIDERS, ANALYTICS_EVENTS } from '@/util/constants'
import { sendEvent } from '@/mixins/eventTransmitter'

const {getCartField, updateCartField} = createHelpers({
  getterType: 'getCartField',
  mutationType: 'updateCartField',
})

// analytics: use centralized sender; no direct GA calls here
async function emitAnalytics(payload = {}, store) {
  if (!payload || !payload.eventName) return
  try {
    await sendEvent([EVENT_PROVIDERS.GA, EVENT_PROVIDERS.LOGZIO], { ...payload, timestamp: new Date().toISOString() }, store)
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[cart] Error emitting analytics:', e)
    }
  }
}

const getDefaultState = () => {
  return {
    buy: {
      salesRep: null,
      items: [],
      selectedPaymentMethod: {
        id: null,
        fee: null,
        fee_waived: null,
        netsuite_item_internalId: null,
      },
      selectedShippingInstructions: {
        id: null,
        name: null,
        netsuite_internalId: null,
      },
      selectedBillingAddress: {
        id: null,
      },
      selectedShippingAddress: {
        id: null,
        country: null,
      },
      selectedDepoAddress: {
        id: null,
      },
      orderNotes: null,
      customerReference: null,
      standardDepoAccountNumber: null,
      standardDepoAttnName: null,
      equityTrustAccountNumber: null,
      equityTrustLinkedAccount: {
        ownerContactName: null,
        accountType: null,
        availableCash: 0,
      },
      equityTrustDepositoryAddress: null,
      fulfillmentType: FulfillmentTypes.STANDARD,
      achDebitDate: null,
      // Retail Ticket Fields
      retailHandlingPercentMarkup: null,
      retailHandlingFlatMarkup: null,
      retailGlobalMarkups: [],
      retailTotal: null,
      waiveHandling: false,
      generateRetailTicket: false,
      // Shipping Hold Fields
      shippingHoldEnabled: false,
      shippingHoldDate: null,
    },
    sell: {
      salesRep: null,
      items: [],
      selectedPaymentMethod: {
        id: null,
        fee: null,
        fee_waived: null,
        netsuite_item_internalId: null,
      },
      selectedBillingAddress: {
        id: null,
      },
      selectedShippingAddress: {
        id: null,
        asset_provider_id: null,
        et_depository_enum: null,
      },
      orderNotes: null,
      customerReference: null,
      processingDelayMessage: null,
      equityTrustAccountNumber: null,
      fulfillmentType: FulfillmentTypes.STANDARD,
      // Retail Ticket Fields
      retailHandlingPercentMarkup: null,
      retailHandlingFlatMarkup: null,
      retailGlobalMarkups: [],
      retailTotal: null,
      waiveHandling: false,
      waivePoHandling: false,
      generateRetailTicket: false,
      preSettlementPreview: null,
    },
    shippingInstructions: [],
    defaultPaymentMethods: [],
  }
}

// initial state
const state = getDefaultState()

// getters
const getters = {
  getCartField,
  getCartByBuySell: (state) => (buySell) => {
    return state[buySell]
  },
  getBuyCart: (state) => {
    return state.buy
  },
  getSellCart: (state) => {
    return state.sell
  },
  getTotalCartCount: (state) => {
    return (
      _.get(state, 'buy.items', []).length +
      _.get(state, 'sell.items', []).length
    )
  },
  getDefaultPaymentMethodsByBuySell: (state) => (buySell) => {
    const scope = buySell === 'sell' ? 'po' : 'so'
    return state.defaultPaymentMethods.filter(
      (paymentMethod) => paymentMethod.scope === scope
    )
  },
  getExistingItemInCart: (state) => (item, buySell) => {
    return state[buySell].items.find((cartItem) => cartItem.item.id === item.id)
  },
  getShippingInstructions: (state) => {
    return state.shippingInstructions.sort((a, b) => {
      return a.priority < b.priority ? -1 : 1
    })
  },
  getCartState: (state) => state,
  getBuyHandlingFee: (state, getters, rootState, rootGetters) => {
    const countries = Object.values(rootGetters.getConfig('COUNTRIES'))

    if (state.buy.waiveHandling) return 0

    return productMixin.methods.getHandlingFee(
      state.buy.items,
      _.get(
        state,
        `buy.selectedShippingAddress.location.country`,
        UNITED_STATES
      ),
      countries
    )
  },
  getSellHandlingFee: (state, getters, rootState, rootGetters) => {
    const subtotal = getters.getSellCart.items.reduce((acc, item) => {
      const itemPrices = productMixin.methods.computePrices(
        item.item,
        rootState.user.tradeTicketSpotPrices,
        getters.getSelectedCompany,
        getters.getSelectedTradingRole,
        {}
      );
      const itemTotal = itemPrices.bid * item.quantity;
      return acc + itemTotal;
    }, 0);
    const threshold = Number(rootGetters.getConfig('PO_HANDLING_FEE_THRESHOLD'));
    const amount = Number(rootGetters.getConfig('PO_HANDLING_FEE_AMOUNT'));
    // suppress fee when company-level waive flag is true OR missing
    // This is to ensure that if the data is stale i.e. not defined, we suppress the fee
    const company = rootGetters.getSelectedCompany || {}
    const waiveFlag = _.get(company, 'waivePoHandling')
    if (waiveFlag === true || waiveFlag === undefined) return 0
    

    if (subtotal < threshold) {
        return amount;
    }

    return 0;
  },
}

// actions
const actions = {
  linkEquityTrustAccount({getters, commit}, accountNumber) {
    // Remove any previously linked account
    commit('SET_ET_LINKED_ACCOUNT', {
      buySell: `buy`,
      linkedAccount: {},
    })
    const selectedCompanyId = getters.getSelectedCompany.id
    return request({
      url: `/companies/${selectedCompanyId}/et/link`,
      method: 'post',
      data: {accountNumber},
    }).then((result) => {
      const linkedAccount = _.get(result, `result.account.response[0]`, {})
      commit('SET_ET_LINKED_ACCOUNT', {
        buySell: `buy`,
        linkedAccount,
      })
      return result
    })
  },
  resetCartState({commit}, data) {
    data.preserveItems = !!data.preserveItems
    commit('RESET_CART_STATE', data)
  },
  async fetchDefaultPaymentMethods({commit}) {
    return request({
      url: '/payment-methods?limit=10000',
      method: 'get',
    }).then((data) => {
      commit(
        'SET_DEFAULT_PAYMENT_METHODS',
        data.data.filter((method) => method.default === true)
      )
    })
  },
  async fetchShippingInstructions({commit}) {
    return request({
      url: '/shipping-instructions?limit=10000',
      method: 'get',
    }).then((data) => {
      commit(
        'SET_SHIPPING_INSTRUCTIONS',
        data.data.filter((row) => row.active)
      )
    })
  },
  async fetchProcessingDelayMessage({commit}) {
    try {
      const data = await request({
        url: '/configurations/processing-delay',
        method: 'get',
      })
      commit('SET_SELL_PROCESSING_DELAY_MESSAGE', data?.processingDelayMessage ?? null)
    } catch {
      commit('SET_SELL_PROCESSING_DELAY_MESSAGE', null)
    }
  },
  async removeItemFromCart({commit, rootGetters, dispatch}, data) {
    commit('REMOVE_ITEM_FROM_CART', data)
    const { item, buySell } = data
    await emitAnalytics({
      eventName: ANALYTICS_EVENTS.REMOVE_FROM_CART,
      item_id: item.item.netsuite_internalId,
      item_name: item.item.name,
      quantity: item.quantity || 0,
      buy_or_sell: buySell,
    }, { getters: rootGetters, dispatch })
  },
  async updateItemQuantity(
    {commit, dispatch, getters, rootState, rootGetters},
    {item, buySell, quantity}
  ) {
    const normalizedQuantity = productMixin.methods.fixNumber(quantity, 8)
    const incrementQty = productMixin.methods.getIncrementQty(item)
    if (incrementQty && normalizedQuantity % incrementQty !== 0) {
      throw new Error(
        `Quantity must be in increments of ${productMixin.methods.fixZeroes(incrementQty)}`
      )
    }

    const limit = buySell === `buy` ? item.sellQuantity : item.buyQuantity
    if (normalizedQuantity > limit)
      throw new Error(
        `You cannot add any more of this product to your ${buySell} trade ticket. Requested quantity: ${normalizedQuantity}, available quantity: ${limit}`
      )
    const existing = getters.getExistingItemInCart(item, buySell)
    if (!existing) {
      // add_to_cart
      await emitAnalytics({
        eventName: ANALYTICS_EVENTS.ADD_TO_CART,
        item_id: item.netsuite_internalId,
        item_name: item.name,
        quantity: normalizedQuantity,
        buy_or_sell: buySell,
      }, { getters: rootGetters, dispatch })
      commit('ADD_ITEM_TO_CART', {item, quantity: normalizedQuantity, buySell})
    } else {
      // update quantity
      await emitAnalytics({
        eventName: ANALYTICS_EVENTS.UPDATE_CART_QUANTITY,
        item_id: item.netsuite_internalId,
        item_name: item.name,
        old_quantity: existing.quantity,
        new_quantity: normalizedQuantity,
        buy_or_sell: buySell,
      }, { getters: rootGetters, dispatch })
    }
    commit('SET_ITEM_IN_CART', {item, quantity: normalizedQuantity, buySell})
    await dispatch('updateProductsInCart')
  },
  async updateItemRetailData(
    {commit, dispatch, getters},
    {
      item,
      buySell,
      retailFlatMarkup,
      retailPercentMarkup,
      retailFlatMarkupRep,
      retailPercentMarkupRep,
    }
  ) {
    const commitPayload = {
      item,
      buySell,
      retailFlatMarkup,
      retailPercentMarkup,
      retailFlatMarkupRep,
      retailPercentMarkupRep,
    }
    commit('UPDATE_ITEM_RETAIL_DATA', commitPayload)
  },
  async updateProductsInCart({getters, dispatch, commit}) {
    const carts = [
      {buySell: 'buy', cart: getters.getBuyCart},
      {buySell: 'sell', cart: getters.getSellCart},
    ]
    const requests = carts.map(({buySell, cart}) => {
      const activelyTransacting = buySell === 'buy' ? `activelySelling` : `activelyBuying`
      const ids = cart.items.map((item) => item.item.id)
      const quantities = cart.items
        .map((item) => ({ id: item.item.id, quantity: item.quantity }))
        .reduce((obj, item) => ({ ...obj, [item.id]: item.quantity }), {})
      if (ids.length === 0) return Promise.resolve()
      const query = JSON.stringify({ id: { $in: ids } })
      return request({
        url: '/products',
        params: {
          companyId: getters.getSelectedCompany.id,
          query,
          quantities: JSON.stringify(quantities),
          limit: 10000,
          offset: 0,
        },
        method: 'get',
      }).then((data) => {
        const cartItems = cart.items
          .map((cartItem) => {
            const item = data.data.rows.find((row) => row.id === cartItem.item.id)
            if (item) {
              const quantity = item[activelyTransacting] ? cartItem.quantity : 0
              return { ...cartItem, item, quantity }
            }
          })
          .filter(Boolean)
        commit('SET_CART', { cartItems, buySell })
      })
    })
    await Promise.all(requests)
  },
}

// mutations
const mutations = {
  updateCartField,
  RESET_CART_STATE(state, {buySell, preserveItems}) {
    // Merge rather than replace so we don't lose observers
    // https://github.com/vuejs/vuex/issues/1118
    const items = [...state[buySell].items]
    const stateToReplace = {[buySell]: getDefaultState()[buySell]}
    if (preserveItems) stateToReplace[buySell].items = items
    Object.assign(state, stateToReplace)
  },
  REMOVE_ITEM_FROM_CART(state, {item, buySell}) {
    _.remove(
      state[buySell].items,
      (itemInCart) => itemInCart.item.id === item.item.id
    )
    state[buySell].items = [...state[buySell].items] // ensure reactive state gets triggered
  },
  ADD_ITEM_TO_CART(state, {item, quantity, buySell}) {
    state[buySell].items.push({
      item: Object.assign({}, item),
      quantity,
    })
  },
  SET_ITEM_IN_CART(state, {item, quantity, buySell}) {
    state[buySell].items = state[buySell].items.map((cartItem) => {
      if (cartItem.item.id === item.id) {
        cartItem.quantity = parseFloat(quantity)
      }
      return cartItem
    })
  },
  UPDATE_ITEM_RETAIL_DATA(
    state,
    {
      item,
      buySell,
      retailFlatMarkup,
      retailPercentMarkup,
      retailFlatMarkupRep,
      retailPercentMarkupRep,
    }
  ) {
    state[buySell].items = state[buySell].items.map((cartItem) => {
      if (cartItem.item.id === item.id) {
        cartItem.retailFlatMarkup = retailFlatMarkup
          ? parseFloat(retailFlatMarkup)
          : null
        cartItem.retailPercentMarkup = retailPercentMarkup
          ? parseFloat(retailPercentMarkup)
          : null
        cartItem.retailFlatMarkupRep = retailFlatMarkupRep
          ? parseFloat(retailFlatMarkupRep)
          : null
        cartItem.retailPercentMarkupRep = retailPercentMarkupRep
          ? parseFloat(retailPercentMarkupRep)
          : null
      }
      return cartItem
    })
  },
  SET_CART(state, {cartItems, buySell}) {
    state[buySell].items = cartItems
  },
  SET_DEFAULT_PAYMENT_METHODS(state, defaultPaymentMethods) {
    state.defaultPaymentMethods = defaultPaymentMethods
  },
  SET_SHIPPING_INSTRUCTIONS(state, shippingInstructions) {
    state.shippingInstructions = shippingInstructions
  },
  SET_ET_LINKED_ACCOUNT(state, {linkedAccount, buySell}) {
    state[buySell].equityTrustLinkedAccount = linkedAccount
  },
  SET_SELL_PROCESSING_DELAY_MESSAGE(state, processingDelayMessage) {
    state.sell.processingDelayMessage = processingDelayMessage
  },
}

export default {
  namespaced: false,
  state,
  getters,
  actions,
  mutations,
}
